// ABOUTME: The desktop cloud stack's scale: v28's size at 1080 x 810, scaled by g, held smaller where it would not fit.
// ABOUTME: The fit keeps the crown under the steersman strip and the stack's base clear of the card.
import { STACK_ROOT, STAGE, STAGE_HORIZON, TIERS, toStage } from '../screens/clouds/stackGeometry';

export const V28_FRAME = { width: 1080, height: 810 } as const;
// v28's crown at the v28 frame is 377.2 px wide, which is this ratio of the head box's 300.
export const V28_CROWN_SCALE = 1.2574;

// The prototype's reach tile at 1080 x 810 is 209.8 px wide, so one tile design pixel (400 to a tile) is this long. The tiles
// keep that size beside the stack at v28's scale and follow the stack only when it is held smaller.
export const V28_REACH_UNIT = 209.8 / 400;

const boxOf = (id: 'head' | 'base') => TIERS.find((tier) => tier.id === id)!.box;

// How far the crown's top stands above the horizon, in stage px at stage scale 1.
export function stackRise(): number {
  const [, y] = boxOf('head');
  return STAGE_HORIZON - toStage(0, y)[1];
}

// How far the base tier's bottom hangs below the horizon (negative when it stands above it).
export function stackDrop(): number {
  const [, y, , h] = boxOf('base');
  return toStage(0, y + h)[1] - STAGE_HORIZON;
}

export function stackG(width: number, height: number): number {
  return Math.min(width / V28_FRAME.width, height / V28_FRAME.height, 1);
}

interface StackRoom {
  width: number;
  height: number;
  line: number;
  shellBottom: number;
  plateTop?: number;
}

// The stack is drawn this many times larger than v28 where the room allows.
export const STACK_GROWTH = 2;

// The scale at which the crown still clears the strip and the base clears the card.
function roomLimit({ line, shellBottom, plateTop }: Pick<StackRoom, 'line' | 'shellBottom' | 'plateTop'>): number {
  const topLimit = (line - shellBottom) / stackRise();
  const drop = stackDrop();
  const bottomLimit = plateTop !== undefined && drop > 0 ? (plateTop - line) / drop : Infinity;
  return Math.min(topLimit, bottomLimit);
}

export function desktopStackScale(room: StackRoom, growth = STACK_GROWTH): number {
  const wanted = growth * (V28_CROWN_SCALE / STACK_ROOT.scale) * stackG(room.width, room.height);
  return Math.min(wanted, roomLimit(room));
}

interface PhoneRoom extends Omit<StackRoom, 'height'> {
  phoneSc: number;
}

// The phone fits the stage to the width; the stack is drawn at twice that where the room under the strip and the width allow.
export function phoneStackScale({ phoneSc, width, ...room }: PhoneRoom): number {
  return Math.min(STACK_GROWTH * phoneSc, roomLimit(room), width / STAGE);
}

// The length of one reach tile design pixel on the desktop for a stack drawn at `stackScale`.
export function desktopReachUnit(stackScale: number): number {
  return V28_REACH_UNIT * (stackScale / (V28_CROWN_SCALE / STACK_ROOT.scale));
}
